import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { getViewerTimezone, formatInstantForViewer } from '../utils/dateTime';
import { formatRiaMessage } from '../utils/formatRiaMessage';
import AskRiaInputBar from '../components/common/AskRiaInputBar';
import riaLogo from '../assets/center-logo.png';
import bulbIcon from '../assets/bulb.png';
import './DashboardPage.css';
import './SpDashboardPage.css';
import { getServiceTypeLabel } from '../utils/serviceTypeLabel';

const STATUS_CFG = {
  confirmed:    { label: 'Confirmed',   color: '#4F46E5' },
  'in-progress':{ label: 'In Progress', color: '#F59E0B' },
  completed:    { label: 'Completed',   color: '#10B981' },
  pending:      { label: 'Pending',     color: '#F59E0B' },
};

// Backend sometimes leaves the requester's display name unresolved and sends
// the raw Firestore document ID instead (e.g. "NOCjI25skgXkydSf7mSE") — a bare
// alphanumeric key with no spaces, unlike any real person's name. Filter those
// out so the UI falls back to a blank (assignee line is simply omitted) rather
// than leaking an internal key. Same pattern already used in JobDetailPage.js
// and JobsPage.js for the same field.
function isRawIdLike(str) {
  return typeof str === 'string' && /^[A-Za-z0-9]{15,36}$/.test(str) && /\d/.test(str);
}
function cleanName(v) {
  return (v && !isRawIdLike(v)) ? v : undefined;
}

// ── 2D SVG icons ──────────────────────────────────────────────────────────────
const ExpandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const BriefcaseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /><line x1="2" y1="13" x2="22" y2="13" />
  </svg>
);

const ClockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const UserPlusIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" /><line x1="17" y1="11" x2="23" y2="11" />
  </svg>
);

// Per-user cache for the SP vendorType (COMPANY/INDIVIDUAL), keyed off the
// email in localStorage's 'user' object so a stale value from a previous
// account on a shared browser can't leak onto a different account's session.
const vendorTypeCacheKey = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    return stored?.email ? `spVendorType:${stored.email}` : null;
  } catch {
    return null;
  }
};

const getCachedVendorType = () => {
  const key = vendorTypeCacheKey();
  return key ? localStorage.getItem(key) : null;
};

const setCachedVendorType = (vendorType) => {
  const key = vendorTypeCacheKey();
  if (!key) return;
  if (vendorType) localStorage.setItem(key, vendorType);
  else localStorage.removeItem(key);
};

// Same per-user cache, for the Company SP's business name — used to greet
// "Hi {businessName}" instead of "Hi {firstName}" for Company vendors only.
const businessNameCacheKey = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    return stored?.email ? `spBusinessName:${stored.email}` : null;
  } catch {
    return null;
  }
};

const getCachedBusinessName = () => {
  const key = businessNameCacheKey();
  return key ? localStorage.getItem(key) : null;
};

const setCachedBusinessName = (businessName) => {
  const key = businessNameCacheKey();
  if (!key) return;
  if (businessName) localStorage.setItem(key, businessName);
  else localStorage.removeItem(key);
};

// ── Main component ────────────────────────────────────────────────────────────
const SpDashboardPage = () => {
  const navigate = useNavigate();
  const riaMessagesEndRef = useRef(null);
  const riaChatAreaRef = useRef(null);

  const [userName, setUserName]           = useState('');
  const [userInfo, setUserInfo]           = useState(null);
  const [riaMessages, setRiaMessages]     = useState([]);
  const [riaInput, setRiaInput]           = useState('');
  const [riaLoading, setRiaLoading]       = useState(false);
  const [riaConversationId, setRiaConversationId] = useState(null);
  const [pendingCount, setPendingCount]   = useState(0);
  const [jobGroups, setJobGroups]         = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  // Full DashboardResponse from GET /api/v1/dashboard — carries the fields the
  // SP-facing profile-completion card and empty-state card read (profileStatus,
  // emptyState, welcomeCard). The "Upcoming Jobs" list itself still comes from
  // the spServices(status) calls below, because the /api/v1/dashboard
  // aggregator is agent/broker-oriented and returns empty `transactionCards`
  // for SERVICE_PROVIDER users.
  const [dashboardData, setDashboardData] = useState(null);
  // Not in localStorage's 'user' object — fetched the same way
  // SpDashboardHeader.js does, to gate the "Invite Inspector" quick action to
  // Company and Individual vendors (same audience as /invite-inspector).
  // Initialized from a per-user cache (see getCachedVendorType below) so the
  // button doesn't pop in a few seconds late waiting on the firebase-token +
  // spProfile round trip on every single dashboard visit — it only has to
  // wait once, the first time this browser sees this account. The effect
  // below still re-fetches every mount and overwrites both the state and
  // the cache, so a real vendorType change (e.g. an admin flips it) heals
  // on the next load instead of sticking to a stale cached value forever.
  const [spVendorType, setSpVendorType]   = useState(getCachedVendorType);
  // Company SP's business name, shown in the greeting instead of firstName.
  // Cached the same way and for the same reason as spVendorType above.
  const [spBusinessName, setSpBusinessName] = useState(getCachedBusinessName);

  // Wait for Firebase auth to be ready and get a fresh token — mirrors
  // DashboardPage's local getFirebaseToken (not a shared utility; see CLAUDE.md)
  const getFirebaseToken = () => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  };

  // Role guard
  useEffect(() => {
    const role = getActiveRoleFromStorage();
    if (role && role.toUpperCase() !== 'SERVICE_PROVIDER') {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // Vendor type + business name — gates the "Invite Inspector" quick action
  // to Company/Individual SPs, and drives the "Hi {businessName}" greeting below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.spProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const vendorType = resp.data?.vendorType || null;
        const businessName = resp.data?.businessName || null;
        if (!cancelled) {
          setSpVendorType(vendorType);
          setSpBusinessName(businessName);
        }
        setCachedVendorType(vendorType);
        setCachedBusinessName(businessName);
      } catch (e) {
        // Non-critical — button/greeting just stay at their cached/default values if this fails.
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // GET /api/v1/dashboard for the SP profile-completion + empty-state cards.
  // Runs in parallel with the spProfile + spServices fetches below — none of
  // them depend on each other. Long timeout (90s) matches DashboardPage.js:
  // the aggregator issues ~50 sequential Firestore queries server-side, which
  // can take ~30s against a local backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        if (!token) return;
        const url = `${API_ENDPOINTS.dashboard}?viewingRole=SERVICE_PROVIDER`;
        const resp = await apiClient.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 90000,
        });
        if (!cancelled) setDashboardData(resp.data || null);
      } catch (e) {
        // Non-critical — the profile-completion / empty-state cards just don't
        // render if this fails. The Upcoming Jobs list (from spServices) is
        // unaffected.
        if (!cancelled) console.warn('[SpDashboard] /api/v1/dashboard failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load first name from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        setUserName(parsed.firstName || '');
        setUserInfo(parsed);
      }
    } catch { /* ignore */ }
  }, []);

  // Fetch dashboard data from API
  useEffect(() => {
    let cancelled = false;

    // Deliberately reuses the component-level getFirebaseToken (line ~77),
    // which waits for Firebase to finish rehydrating the session via
    // onAuthStateChanged. A local shadow copy here used to bail out
    // immediately with null whenever auth.currentUser wasn't populated yet
    // (the normal, momentary state on a hard page reload) — fetchDashboard
    // would then silently give up and never retry, leaving this panel blank
    // on reload even though the fix above (real endpoint + correct mapping)
    // was working fine once auth was actually ready.

    // Day-bucketed in the viewer's own profile timezone (not the device's) —
    // matches every other "Today"/"Tomorrow" grouping in the app.
    const computeGroupLabel = (dateStr) => {
      if (!dateStr) return 'Upcoming';
      const d = new Date(dateStr);
      if (isNaN(d)) return 'Upcoming';
      const zone = getViewerTimezone();
      const dayKey = (x) => x.toLocaleDateString('en-CA', { timeZone: zone });
      const today = new Date();
      const tomorrow = new Date(Date.now() + 86400000);
      if (dayKey(d) === dayKey(today))    return 'Today';
      if (dayKey(d) === dayKey(tomorrow)) return 'Tomorrow';
      return 'Later this week';
    };

    // requestedTime/scheduledTime come back as raw ISO datetimes (e.g.
    // "2026-08-25T03:21:00Z") — was being dropped straight into the meta
    // line unformatted, showing the raw timestamp string in the UI. Shows
    // both date and time (e.g. "Aug 25, 3:21 AM") — the group header
    // ("Today"/"Tomorrow"/"Later this week") is only relative, so a
    // "Later this week" row still needs its own concrete date. Rendered in
    // the viewer's own profile timezone, not the device's — the previous fix
    // here only added local formatting, it never added zone conversion.
    const formatMetaDateTime = (isoStr) => {
      if (!isoStr) return '';
      const { dateLabel, timeLabel } = formatInstantForViewer(isoStr);
      if (!dateLabel) return '';
      return timeLabel ? `${dateLabel}, ${timeLabel}` : dateLabel;
    };

    // Split into two stacked lines — mirrors the Agent Upcoming Tasks panel's
    // layout (due-text line + person-icon assignee line) instead of cramming
    // service type + date/time + requester into one joined string.
    const buildJobDueLine = (job) => {
      const parts = [
        getServiceTypeLabel(job) || job.type || '',
        formatMetaDateTime(job.requestedTime || job.scheduledTime),
      ].filter(Boolean);
      return parts.join(' · ');
    };

    const jobAssignee = (job) =>
      cleanName(job.requesterName) || cleanName(job.requestedBy) || cleanName(job.agentName) || cleanName(job.clientName) || '';

    // Real ServiceRequestResponse statuses -> the STATUS_CFG keys the render
    // below actually looks up (STATUS_CFG has no ACCEPTED/IN_PROGRESS/etc.
    // entries — it's keyed by display concept, not the raw backend enum).
    const STATUS_TO_CFG_KEY = {
      PENDING:              'pending',
      AWAITING_ASSIGNMENT:  'pending',
      ACCEPTED:             'confirmed',
      SCHEDULED:            'confirmed',
      IN_PROGRESS:          'in-progress',
      COMPLETED:            'completed',
    };

    const fetchDashboard = async () => {
      try {
        const token = await getFirebaseToken();
        if (!token) { if (!cancelled) setDashboardLoading(false); return; }
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

        // API_ENDPOINTS.spDashboard (/api/v1/service-providers/dashboard) is a
        // dead legacy alias that routes to an unrelated backend service (see
        // api.js's own comment on the spServices/serviceRequestTimeline
        // block) — it 404s, and the old catch-and-swallow below silently left
        // this panel empty forever. Use the same real, already-proven
        // endpoint JobsPage.js/JobDetailPage.js use instead.
        //
        // AWAITING_ASSIGNMENT is the natural inbox state for a Company SP
        // (jobs arrive routed to the Company itself until the admin picks a
        // staff inspector). Fetch it alongside so those jobs surface both in
        // the "new job requests" pending badge and in the Upcoming list —
        // otherwise a fresh Company SP that hasn't routed anything yet sees
        // an empty dashboard even when there's work waiting. Individual SPs
        // never hold this status, so the extra fetch is a no-op for them.
        const [pendingResp, awaitingResp, acceptedResp, scheduledResp, inProgressResp] = await Promise.all([
          apiClient.get(API_ENDPOINTS.spServices('PENDING'), authHeaders).catch(() => null),
          apiClient.get(API_ENDPOINTS.spServices('AWAITING_ASSIGNMENT'), authHeaders).catch(() => null),
          apiClient.get(API_ENDPOINTS.spServices('ACCEPTED'), authHeaders).catch(() => null),
          apiClient.get(API_ENDPOINTS.spServices('SCHEDULED'), authHeaders).catch(() => null),
          apiClient.get(API_ENDPOINTS.spServices('IN_PROGRESS'), authHeaders).catch(() => null),
        ]);
        if (cancelled) return;

        const extractArr = (resp) => Array.isArray(resp?.data) ? resp.data : (resp?.data?.content || resp?.data?.data || []);

        // Match JobsPage.js's Requests tab semantics: pending count is
        // PENDING + AWAITING_ASSIGNMENT (both are "action required").
        setPendingCount(extractArr(pendingResp).length + extractArr(awaitingResp).length);

        // "Upcoming" = anything the SP needs to move on, not yet done.
        // Mirrors JobsPage's action-required + active states so a Company
        // admin with jobs still in AWAITING_ASSIGNMENT sees them here.
        const rawJobs = [
          ...extractArr(awaitingResp),
          ...extractArr(pendingResp),
          ...extractArr(acceptedResp),
          ...extractArr(scheduledResp),
          ...extractArr(inProgressResp),
        ];

        // Group by day label
        const grouped = {};
        rawJobs.forEach((job, i) => {
          const label = job.groupLabel ||
            computeGroupLabel(job.requestedDate || job.scheduledAt || job.scheduledDate || job.date);
          if (!grouped[label]) grouped[label] = [];
          grouped[label].push({
            id:       job.id || `job-${i}`,
            address:  job.propertyAddress || job.address || '',
            dueLine:  buildJobDueLine(job),
            assignee: jobAssignee(job),
            status:   STATUS_TO_CFG_KEY[(job.status || '').toUpperCase()] || (job.status || 'pending').toLowerCase().replace(/\s+/g, '-'),
          });
        });

        const ORDER = ['Today', 'Tomorrow', 'Later this week'];
        const groups = [
          ...ORDER.filter(l => grouped[l]).map(l => ({ label: l, jobs: grouped[l] })),
          ...Object.keys(grouped).filter(l => !ORDER.includes(l)).map(l => ({ label: l, jobs: grouped[l] })),
        ];
        setJobGroups(groups);
      } catch { /* show empty state on error */ } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    };

    fetchDashboard();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll Ria chat area — scroll only the chat container, never the window
  // (mirrors DashboardPage so both dashboards behave identically)
  useEffect(() => {
    if (riaChatAreaRef.current && riaMessages.length > 0) {
      riaChatAreaRef.current.scrollTop = riaChatAreaRef.current.scrollHeight;
    }
  }, [riaMessages]);

  // Load most recent Ria conversation on mount so prior chat history shows by
  // default (mirrors DashboardPage.loadRiaHistory / mobile RiaService.loadLatestConversation)
  useEffect(() => {
    const loadRiaHistory = async () => {
      try {
        const firebaseToken = await getFirebaseToken();

        const listResp = await apiClient.get(API_ENDPOINTS.riaConversations, {
          headers: { Authorization: `Bearer ${firebaseToken}` },
          params: { limit: 1 },
        });
        const conversations = listResp.data?.conversations || [];
        if (conversations.length === 0) return;

        const latestConvId = conversations[0].id;
        const detailResp = await apiClient.get(API_ENDPOINTS.riaConversationById(latestConvId), {
          headers: { Authorization: `Bearer ${firebaseToken}` },
        });
        const conv = detailResp.data?.conversation;
        if (!conv) return;

        const normalized = (conv.messages || []).map((msg) => ({
          role: msg.role === 'user' ? 'user' : 'ria',
          content: msg.content,
          time: msg.created_at
            ? new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: getViewerTimezone() })
            : '',
        }));

        // Don't clobber messages the user already started sending while history was loading
        setRiaMessages((prev) => (prev.length === 0 ? normalized : prev));
        setRiaConversationId((prev) => prev || conv.id);
      } catch (error) {
        console.warn('Failed to load Ria conversation history:', error?.message);
      }
    };

    loadRiaHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ria submit ──────────────────────────────────────────────────────────────
  const handleRiaSubmit = async () => {
    const prompt = riaInput.trim();
    if (!prompt || riaLoading) return;

    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: getViewerTimezone() });

    setRiaInput('');
    setRiaMessages((prev) => [...prev, { role: 'user', content: prompt, time: timeStr }]);
    setRiaLoading(true);

    try {
      const firebaseToken = await getFirebaseToken();

      const requestBody = {
        message: prompt,
        conversation_id: riaConversationId || undefined,
        context: {},
      };

      const response = await apiClient.post(
        API_ENDPOINTS.riaChat,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json',
          },
          // Ria answers with tool calls take ~40s against the local backend
          // (fast on GKE); the 30s apiClient default aborts before they finish.
          timeout: 120000,
        }
      );

      if (response.data?.conversation_id) {
        setRiaConversationId(response.data.conversation_id);
      }

      const riaReply = response.data?.message || response.data?.response || response.data?.content || response.data?.answer || 'No response from Ria.';
      const riaTime = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: getViewerTimezone() });
      setRiaMessages((prev) => [...prev, { role: 'ria', content: riaReply, time: riaTime }]);
    } catch (error) {
      console.error('Error asking Ria:', error);
      const errTime = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: getViewerTimezone() });
      setRiaMessages((prev) => [
        ...prev,
        { role: 'ria', content: "Sorry, I couldn't process your request. Please try again.", time: errTime },
      ]);
    } finally {
      setRiaLoading(false);
    }
  };

  const QUICK_ACTIONS = [
    { label: 'View my pending jobs',         tab: 'requests',  icon: <BriefcaseIcon /> },
    { label: 'Active jobs',                  tab: 'active',    icon: <ClockIcon /> },
    { label: 'Show completed jobs',          tab: 'completed', icon: <CheckCircleIcon /> },
  ];

  // Profile Completion card — mirrors SP-mobile HomePage.renderProfileCompletionCard
  // (which itself mirrors rialty-mobile-app/src/screens/agent/DashboardScreen.tsx).
  // Circumference math: r=24 → C = 2π·24 ≈ 150.796; the ring fills clockwise
  // via `strokeDashoffset = C · (1 - pct/100)`.
  const renderProfileCompletionCard = () => {
    const ps = dashboardData?.profileStatus;
    if (!ps || ps.isComplete) return null;
    const pct = Math.max(0, Math.min(100, ps.completionPercentage ?? 0));
    const CIRC = 2 * Math.PI * 24;
    const incomplete = (ps.checklist || []).filter((i) => !i.isComplete);
    const goToProfile = () => navigate('/profile/sp');
    return (
      <div className="spd-profile-card">
        <div className="spd-profile-header">
          <div className="spd-profile-info">
            <h3 className="spd-profile-title">Complete Your Profile</h3>
            <p className="spd-profile-subtitle">{pct}% complete</p>
          </div>
          <div className="spd-profile-ring">
            <svg width="56" height="56" viewBox="0 0 56 56">
              <circle cx="28" cy="28" r="24" stroke="#F3F0FB" strokeWidth="4" fill="none" />
              <circle
                cx="28" cy="28" r="24"
                stroke="#381F83" strokeWidth="4" fill="none" strokeLinecap="round"
                strokeDasharray={CIRC}
                strokeDashoffset={CIRC * (1 - pct / 100)}
                transform="rotate(-90 28 28)"
              />
            </svg>
            <span className="spd-profile-ring-pct">{pct}%</span>
          </div>
        </div>
        <div className="spd-profile-bar-bg">
          <div className="spd-profile-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="spd-profile-checklist">
          {incomplete.map((item) => (
            <button
              key={item.id}
              type="button"
              className="spd-profile-checklist-item"
              onClick={goToProfile}
            >
              <span className="spd-profile-checklist-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DBCEFF" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                </svg>
              </span>
              <span className="spd-profile-checklist-content">
                <span className="spd-profile-checklist-label">{item.label}</span>
                <span className="spd-profile-checklist-desc">{item.description}</span>
              </span>
              {item.actionUrl ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DBCEFF" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              ) : null}
            </button>
          ))}
        </div>
        {ps.nextStep ? (
          <button type="button" className="spd-profile-next-btn" onClick={goToProfile}>
            {ps.nextStep.label}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        ) : null}
      </div>
    );
  };

  // Empty-state card — shown when the SP has a complete profile but no
  // upcoming jobs from the spServices calls. Trigger keys off local jobGroups
  // rather than transactionSummary.hasTransactions because /api/v1/dashboard's
  // transactionSummary is agent/broker-oriented and always false for SPs.
  // Label is hard-coded to "View Jobs" (SP-facing wording) — the backend's
  // `emptyState.cta.label` reads "View Transactions" which doesn't fit here.
  const renderEmptyStateCard = () => {
    const ps = dashboardData?.profileStatus;
    const es = dashboardData?.emptyState;
    if (!ps?.isComplete || !es) return null;
    if (jobGroups.length > 0) return null;
    return (
      <div className="spd-empty-card">
        <svg width="47" height="47" viewBox="0 0 24 24" fill="none" stroke="#DBCEFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
        <h3 className="spd-empty-title">{es.title}</h3>
        <p className="spd-empty-desc">{es.description}</p>
        <button
          type="button"
          className="spd-empty-cta"
          onClick={() => navigate('/jobs')}
        >
          View Jobs
        </button>
      </div>
    );
  };

  // Company SPs are greeted by their business name instead of their first
  // name (an Individual SP has no company, so falls back to the usual name).
  const greetingName = (spVendorType === 'COMPANY' && spBusinessName) ? spBusinessName : userName;
  const greeting = greetingName ? `Hi ${greetingName}, what would you like to do today?` : 'What would you like to do today?';

  return (
    <div className="dashboard-exact-container spd-page">
      <main className="dashboard-exact-main">

        {/* ── Greeting — centered via .spd-page .dashboard-exact-greeting in
             SpDashboardPage.css ── */}
        <h1 className="dashboard-exact-greeting">{greeting}</h1>

        {/* ── Ria AI box ── */}
        <div className="dashboard-exact-search-container">
          <div className="dashboard-exact-search-card">
            <button className="dashboard-exact-expand-btn" aria-label="Expand">
              <ExpandIcon />
            </button>

            <div className="dashboard-ria-chat-area" ref={riaChatAreaRef}>
              {riaMessages.map((msg, index) => (
                <div
                  key={index}
                  className={`dashboard-ria-message ${msg.role === 'user' ? 'dashboard-ria-message-user' : 'dashboard-ria-message-ria'}`}
                >
                  {msg.role === 'ria' && (
                    <div className="dashboard-ria-avatar">
                      <img src={riaLogo} alt="Ria" className="dashboard-ria-avatar-img" />
                    </div>
                  )}
                  <div className="dashboard-ria-message-bubble">
                    {msg.role === 'user' && (
                      <span className="dashboard-ria-message-sender">{userName}, {msg.time}</span>
                    )}
                    {msg.role === 'ria' && (
                      <span className="dashboard-ria-message-sender">Ria, {msg.time}</span>
                    )}
                    <div className="dashboard-ria-message-content">
                      {msg.role === 'ria' ? formatRiaMessage(msg.content) : msg.content}
                    </div>
                  </div>
                  {msg.role === 'user' && (
                    <div className="dashboard-ria-user-avatar">
                      {userInfo?.profilePhotoBase64 || userInfo?.profilePhotoUrl ? (
                        <img
                          src={userInfo.profilePhotoBase64 || userInfo.profilePhotoUrl}
                          alt="User"
                          className="dashboard-ria-user-avatar-img"
                        />
                      ) : (
                        <span className="dashboard-ria-user-avatar-initials">
                          {(userInfo?.firstName?.[0] || '').toUpperCase()}
                          {(userInfo?.lastName?.[0] || '').toUpperCase()}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {riaLoading && (
                <div className="dashboard-ria-message dashboard-ria-message-ria">
                  <div className="dashboard-ria-avatar">
                    <img src={riaLogo} alt="Ria" className="dashboard-ria-avatar-img" />
                  </div>
                  <div className="dashboard-ria-message-content dashboard-ria-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={riaMessagesEndRef} />
            </div>

            <AskRiaInputBar
              value={riaInput}
              onChange={setRiaInput}
              onSend={handleRiaSubmit}
              placeholder="Ask Ria..."
              loading={riaLoading}
              disabled={riaLoading || !riaInput.trim()}
            />
          </div>

          <div className="dashboard-exact-search-tip">
            <img src={bulbIcon} alt="tip" width="16" height="16" />
            <span>Tip: You can say &ldquo;Ria, show me my jobs this week&rdquo;</span>
          </div>
        </div>

        {/* Profile Completion Card — shows when the SP hasn't finished onboarding.
            Sourced from GET /api/v1/dashboard's profileStatus payload. */}
        {renderProfileCompletionCard()}

        {/* ── Bottom grid ── */}
        <div className="dashboard-exact-content-grid main">

          {/* LEFT — Upcoming Jobs */}
          <div className="dashboard-exact-section inner1">
            <h2 className="dashboard-exact-section-title">Upcoming Jobs</h2>

            {/* Pending requests alert */}
            {pendingCount > 0 && (
              <div
                className="spd-req-banner"
                role="button"
                tabIndex={0}
                onClick={() => navigate('/jobs')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/jobs')}
              >
                <div className="spd-rbl">
                  <AlertCircleIcon />
                  <div>
                    <p className="spd-rb-t">{pendingCount} new job {pendingCount === 1 ? 'request' : 'requests'}</p>
                    <p className="spd-rb-s">Action required — accept or decline</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="spd-rb-btn"
                  onClick={(e) => { e.stopPropagation(); navigate('/jobs'); }}
                >
                  Review
                </button>
              </div>
            )}

            <div className="dashboard-exact-tasks-list">
              {dashboardLoading ? (
                <div className="dashboard-upcoming-loading">
                  Loading
                  <span className="dashboard-upcoming-loading-dot">.</span>
                  <span className="dashboard-upcoming-loading-dot">.</span>
                  <span className="dashboard-upcoming-loading-dot">.</span>
                </div>
              ) : jobGroups.length === 0 ? (
                // When /api/v1/dashboard has returned a `profileStatus.isComplete
                // && emptyState` payload, render the shared empty-state card
                // (SP-mobile parity, with "View Jobs" CTA → /jobs). Otherwise
                // fall back to the original hardcoded block so the panel isn't
                // blank while the dashboard call is still in flight or if it
                // failed.
                renderEmptyStateCard() || (
                  <div className="dashboard-exact-empty-state">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                      <path d="M9 11l3 3L22 4" />
                      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                    </svg>
                    <h3 className="dashboard-exact-empty-title">No Upcoming Jobs</h3>
                    <p className="dashboard-exact-empty-description">
                      Your scheduled jobs will appear here once they're assigned.
                    </p>
                  </div>
                )
              ) : (
                jobGroups.map((group) => (
                  // Fragment (not a wrapping div) so the day-group label and each job row
                  // stay direct children of .dashboard-exact-tasks-list — that's what lets
                  // its "gap: 10px" flex spacing (same rule the Agent dashboard's Upcoming
                  // Tasks list relies on) apply between every row, matching that card design
                  // instead of rows within a group rendering flush against each other.
                  <React.Fragment key={group.label}>
                    <p className="spd-slbl">{group.label}</p>
                    {group.jobs.map((job) => {
                      const cfg = STATUS_CFG[job.status] || { label: job.status, color: '#8b5cf6' };
                      return (
                        <div
                          key={job.id}
                          className="dashboard-exact-task-item"
                          style={{ borderLeftColor: cfg.color, cursor: 'pointer' }}
                          onClick={() => navigate(`/job/${job.id}`)}
                        >
                          <div className="dashboard-exact-task-content">
                            <div className="dashboard-exact-task-title">
                              <span className="dashboard-upcoming-chip dashboard-upcoming-chip--job">Job</span>
                              {job.address}
                            </div>
                            <span className="dashboard-exact-task-status">
                              <span className="dashboard-exact-status-dot" style={{ backgroundColor: cfg.color }}></span>
                              {cfg.label}
                            </span>
                            <div className="dashboard-exact-task-meta-stack">
                              {job.dueLine && (
                                <span className="dashboard-exact-task-due">{job.dueLine}</span>
                              )}
                              {job.assignee && (
                                <span className="dashboard-exact-task-assigned">
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                                  </svg>
                                  {job.assignee}
                                </span>
                              )}
                            </div>
                            <button
                              className="dashboard-exact-task-action"
                              style={{
                                backgroundColor: 'rgba(217, 213, 236, 0.7)',
                                color: '#381F83',
                                marginLeft: 'auto',
                                borderRadius: '20px',
                                border: '1px solid rgba(75, 63, 160, 0.15)',
                              }}
                              onClick={(e) => { e.stopPropagation(); navigate(`/job/${job.id}`); }}
                            >
                              <ViewIcon />
                              View
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>

          {/* RIGHT — Quick Actions */}
          <div className="dashboard-exact-section inner2">
            <h2 className="dashboard-exact-section-title">Quick Actions</h2>
            <div className="dashboard-exact-actions-list">
              {QUICK_ACTIONS.map(({ label, tab, icon }) => (
                <button
                  key={label}
                  type="button"
                  className="dashboard-exact-action-btn"
                  onClick={() => navigate('/jobs', { state: { tab } })}
                >
                  <span className="dashboard-exact-action-icon">{icon}</span>
                  <span className="dashboard-exact-action-label">{label}</span>
                </button>
              ))}
              {(spVendorType === 'COMPANY' || spVendorType === 'INDIVIDUAL') && (
                <button
                  type="button"
                  className="dashboard-exact-action-btn"
                  onClick={() => navigate('/invite-inspector')}
                >
                  <span className="dashboard-exact-action-icon"><UserPlusIcon /></span>
                  <span className="dashboard-exact-action-label">
                    {spVendorType === 'COMPANY' ? 'Invite Someone' : 'Invite Inspector'}
                  </span>
                </button>
              )}
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default SpDashboardPage;
